import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Sparkles, Key, X } from 'lucide-react';

const Login = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const [newTempPassword, setNewTempPassword] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Salvar token no sessionStorage
        sessionStorage.setItem('auth_token', data.token);
        sessionStorage.setItem('auth_user', data.user);
        sessionStorage.setItem('auth_timestamp', Date.now().toString());
        
        // Redirecionar: Mobile vai direto para Digisat, Desktop vai para Admin
        const isMobile = window.innerWidth <= 768;
        navigate(isMobile ? '/digisat' : '/admin');
      } else {
        setError(data.detail || 'Senha incorreta. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro no login:', err);
      setError('Erro ao conectar com servidor. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');
    setResetLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setNewTempPassword(data.temporary_password);
        setResetSuccess('✅ Nova senha temporária gerada com sucesso!');
      } else {
        setResetError(data.detail || 'Erro ao resetar senha. Verifique o email.');
      }
    } catch (err) {
      console.error('Erro ao resetar senha:', err);
      setResetError('Erro ao conectar com servidor. Tente novamente.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-500 to-red-500 p-4">
      {/* Background animado */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-300 opacity-10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Card de Login */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mb-4 shadow-lg">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-gray-800 mb-2">
            MultiSales IA
          </h1>
          <p className="text-gray-600 text-sm">
            Área Administrativa
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className="space-y-6">
          {/* Campo Senha */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Senha de Acesso
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Digite sua senha"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mensagem de Erro */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-700 text-sm font-medium">
                ❌ {error}
              </p>
            </div>
          )}

          {/* Botão Login */}
          <button
            type="submit"
            disabled={loading || !password}
            className={`w-full py-3 px-4 rounded-lg font-bold text-white transition-all transform hover:scale-105 ${
              loading || !password
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Verificando...
              </span>
            ) : (
              'Entrar'
            )}
          </button>

          {/* Link Esqueci minha senha */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgotModal(true)}
              className="text-sm text-purple-600 hover:text-purple-800 font-semibold hover:underline"
            >
              🔑 Esqueci minha senha
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            🔒 Acesso restrito a administradores autorizados
          </p>
        </div>
      </div>

      {/* Modal: Esqueci minha senha */}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative">
            {/* Botão Fechar */}
            <button
              onClick={() => {
                setShowForgotModal(false);
                setResetEmail('');
                setResetError('');
                setResetSuccess('');
                setNewTempPassword('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mb-4 shadow-lg">
                <Key className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black text-gray-800 mb-2">
                Recuperar Senha
              </h2>
              <p className="text-gray-600 text-sm">
                Digite seu email para gerar nova senha temporária
              </p>
            </div>

            {!newTempPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {/* Campo Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email do Administrador
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="block w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                {/* Mensagem de Erro */}
                {resetError && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <p className="text-red-700 text-sm font-medium">
                      ❌ {resetError}
                    </p>
                  </div>
                )}

                {/* Botão Resetar */}
                <button
                  type="submit"
                  disabled={resetLoading || !resetEmail}
                  className={`w-full py-3 px-4 rounded-lg font-bold text-white transition-all ${
                    resetLoading || !resetEmail
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg'
                  }`}
                >
                  {resetLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Gerando nova senha...
                    </span>
                  ) : (
                    'Gerar Nova Senha'
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                {/* Sucesso */}
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-green-700 text-sm font-medium mb-2">
                    {resetSuccess}
                  </p>
                </div>

                {/* Nova Senha */}
                <div className="bg-gradient-to-r from-orange-100 to-red-100 p-6 rounded-lg border-2 border-orange-300">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    🔑 Sua nova senha temporária:
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-gray-300 mb-3">
                    <p className="text-2xl font-mono font-bold text-center text-orange-600 select-all">
                      {newTempPassword}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 text-center">
                    📋 Clique para selecionar e copie esta senha
                  </p>
                </div>

                {/* Instruções */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-800 font-medium mb-2">
                    💡 Próximos passos:
                  </p>
                  <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Copie a senha temporária acima</li>
                    <li>Feche este modal</li>
                    <li>Use a nova senha para fazer login</li>
                    <li>Recomendado: altere para uma senha permanente no painel</li>
                  </ol>
                </div>

                {/* Botão Fechar */}
                <button
                  onClick={() => {
                    setShowForgotModal(false);
                    setResetEmail('');
                    setResetSuccess('');
                    setNewTempPassword('');
                  }}
                  className="w-full py-3 px-4 rounded-lg font-bold text-white bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-lg"
                >
                  Entendi, fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
